import type { CustomFieldDefinition, CustomFieldValueInput } from "@/lib/backend/api";

/**
 * Payload shape for a single custom field value on an application.
 *
 * The generated `CustomFieldValueInput` doesn't carry the FILE variant: the api accepts a base64
 * data-URL under `file` and turns it into an upload server-side, but the generated client types
 * only describe the scalar variants.
 */
export type CustomFieldValuePayload = CustomFieldValueInput & { file?: string };

/**
 * True when a FILE value is a base64 data-URL, the only shape the api can store (#117).
 *
 * `CustomFieldFile` with `base64` reads the chosen file into a `data:<mime>;base64,…` string.
 * Without that prop the value stays a raw `File`, which cannot be sent as JSON — so a `File` counts
 * as "no usable value" here rather than as a filled field.
 */
function isSendableFileValue(value: any): value is string {
  return typeof value === "string" && value.startsWith("data:");
}

/**
 * True when a form value should count as "not filled in" for required-field validation.
 *
 * Kept next to {@link buildCustomFieldValueInput} on purpose: a value that yields an empty payload
 * must also fail validation, otherwise a required field passes on the client and then fails
 * server-side without pointing at the field (#117).
 */
export function isEmptyCustomFieldValue(type: CustomFieldDefinition["type"], value: any): boolean {
  if (type === "FILE") {
    return !isSendableFileValue(value);
  }

  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * True when a file was chosen but is not (yet) sendable — it is still a raw `File`.
 *
 * `CustomFieldFile` parks the chosen `File` in the field value and replaces it with the base64
 * data-URL once `FileReader` is done. Submitting inside that window would otherwise drop the file
 * from the payload without telling anyone, so callers block on this instead (#117).
 */
export function isPendingFileValue(type: CustomFieldDefinition["type"], value: any): boolean {
  // Deliberately narrow: only a raw File means "picked, still converting". Any other non-sendable
  // shape (such as the api's { file, fileUrl } for an already stored file) is not a pending read.
  return type === "FILE" && value instanceof File;
}

/** Builds a CustomFieldValueInput object from a raw form value and field type */
export function buildCustomFieldValueInput(
  type: CustomFieldDefinition["type"],
  value: any,
): CustomFieldValuePayload {
  // FILE is the odd one out: the api expects only `{ file: "data:<mime>;base64,…" }`, the same
  // shape the registration flow sends (accept-invitation.form.tsx). An unusable value yields an
  // empty payload, which the caller skips instead of posting an empty field.
  if (type === "FILE") {
    return isSendableFileValue(value) ? { file: value } : {};
  }

  const input: CustomFieldValuePayload = {
    text: null,
    number: null,
    date: null,
    select: null,
    multiSelect: null,
    boolean: null,
  };

  switch (type) {
    case "TEXT":
      input.text = value || null;
      break;
    case "NUMBER":
      // Handle number: null or undefined becomes null, otherwise convert to number
      // This properly handles 0 as a valid value
      input.number = value === null || value === undefined || value === "" ? null : Number(value);
      break;
    case "DATE":
      input.date = value || null;
      break;
    case "SELECT":
      input.select = value || null;
      break;
    case "MULTI_SELECT":
      input.multiSelect = Array.isArray(value) ? (value as string[]) : [];
      break;
    case "BOOLEAN":
      input.boolean = Boolean(value);
      break;
  }

  return input;
}

/**
 * True when any value in a form is a file that is still being read.
 *
 * The counterpart to {@link isPendingFileValue}, for the submit button rather than the submit
 * handler. Blocking afterwards — validate, then say "het bestand wordt nog verwerkt" — means the
 * race has already been entered: the click lands while `FileReader` is running, the handler awaits
 * a network call, and the read finishes somewhere in the middle. Disabling the button instead means
 * that window cannot be entered at all.
 *
 * Deliberately type-blind: it asks the values, not the definitions, so a caller does not have to
 * have loaded the custom field definitions before it can render its button. A raw `File` in a form
 * value only ever means one thing here — `CustomFieldFile` parked it and the read is still
 * running.
 */
export function hasPendingFileRead(values: Record<string, unknown> | undefined): boolean {
  if (!values) {
    return false;
  }

  return Object.values(values).some((value) => value instanceof File);
}
